import {call, put, race, take, select, delay} from 'redux-saga/effects';
import {
  apiActionFailed,
  apiActionSucceeded,
  setNetworkIsDownFlag
} from '../actionCreators';
import getHeadersFromSuccessAction from '../helpers/getHeadersFromSuccessAction';
import getStatusCode from '../helpers/getStatusCode';
import getHeaderValueFromError from '../helpers/getHeaderValueFromError';
import apiFn from '../helpers/apiFn';
import getWidgetId from '../selectors/getWidgetId';
import getTraceId from '../helpers/getTraceId';
import ERROR_CODES from '../errorCodes/errorCodes';
import addErrorResponseData from '../errorCodes/addErrorResponseData';
import {POPUP_HIDE_DELAY} from '../../notifications/constants';
import {
  showNotification,
  hideNotification
} from '../../notifications/actionCreators';
import isNotificationHideAction from '../../notifications/helpers/isNotificationHideAction';
import addSystemNotification from './addSystemNotification';
import {HTTP_STATUS_CODES} from '../constants';
import {isWAFBlocked} from '../helpers/isWAFBlocked';

export default injections => {
  /**
   * Generic api action
   * @param method http verb
   * @param url request url
   * @param key redux state key for status and response data
   * @param data request payload
   * @param setKey additional redux state key where data is set after success
   * @param silent number/boolean: if true, won't show error dialog on fail.
   *               if number, won't show error dialog for that response status code
   * @param popup Object with 'text' field to show as popup on success
   * @param responseDataTransform function (object -> object) that transforms api response data to another form
   * @param silentWAF don't show error dialog if WAF is blocking the request
   */
  function* doApiAction(action) {
    const {
      method,
      url,
      key,
      data,
      setKey,
      silent,
      popup,
      responseDataTransform,
      silentWAF = false
    } = action.payload;
    const errorHandler = injections?.errorHandler;
    if (!url) {
      // for development. user should not endup here
      throw new Error('doApiAction: Did you forget url?');
    }

    try {
      // DELETE needs extra wrapping for data in request body in axios
      const requestBody = method === 'delete' ? {data} : data;
      const response = yield call(apiFn, {method, url, data: requestBody});
      const payload = {
        key,
        response:
          responseDataTransform && response.data
            ? {...response, data: responseDataTransform(response.data)}
            : response
      };
      yield put(apiActionSucceeded(payload));
      const headers = payload.response.headers;
      if (headers) {
        const location = getHeadersFromSuccessAction({payload}, 'location');
        if (location) {
          yield put(apiActionSucceeded({key: 'location', response: location}));
        }
      }
      // Handle system wide errors/notifications
      const errors = getHeadersFromSuccessAction({payload}, 'tmt-error');

      if (errors) {
        const errorCodes = errors.split(',');
        yield* errorCodes.map(addSystemNotification);
      }

      if (setKey) {
        yield put(
          apiActionSucceeded({key: setKey, response: payload.response})
        );
      }
      if (popup) {
        yield put(showNotification(key, 'success', popup));
        const {expired} = yield race({
          expired: call(delay, POPUP_HIDE_DELAY),
          closed: take(isNotificationHideAction(key, true))
        });
        if (expired) {
          yield put(hideNotification(key, true));
        }
      }
    } catch (err) {
      yield put(apiActionFailed({key, error: err}));

      let fallbackToDefaultErrorHandling = true;
      if (errorHandler) {
        fallbackToDefaultErrorHandling = yield errorHandler(err);
      }
      if (fallbackToDefaultErrorHandling) {
        const statusCode = getStatusCode(err);
        const tmtErrorCode = getHeaderValueFromError(err, 'tmt-error');
        // if silent we do not need to show error dialog
        const expectedStatusCode =
          typeof silent === 'number' && statusCode === silent;

        if (
          silentWAF &&
          isWAFBlocked(statusCode, err?.response?.headers ?? {})
        ) {
          // TODO: silently (= w/o error dialog) report error to server
          // Currently not possible https://confluence.ahtp.fi/pages/viewpage.action?pageId=43483766
        } else if (!silent && !expectedStatusCode) {
          // show error dialog
          const errorCode =
            statusCode === HTTP_STATUS_CODES.FORBIDDEN
              ? ERROR_CODES.FORBIDDEN
              : tmtErrorCode === ERROR_CODES.JOB_POSTING_REFERENCE
              ? ERROR_CODES.JOB_POSTING_REFERENCE
              : err.response
              ? ERROR_CODES.GENERAL
              : ERROR_CODES.NETWORK_ERROR;
          const widgetId = yield select(getWidgetId);
          const traceId = err.response ? getTraceId(err.response) : '';
          const errorData = addErrorResponseData(
            key,
            widgetId,
            traceId,
            err.response,
            errorCode
          );

          if (errorCode === ERROR_CODES.NETWORK_ERROR) {
            yield put(setNetworkIsDownFlag());
          }

          yield put(showNotification('error', errorData));
        }
      }
    }
  }
  return doApiAction;
};
